/* ca-nyfw-marquee — gap-free, constant-speed marquee engine.
 *
 * WHY THIS EXISTS
 * The previous marquees animated a fixed number of segments from translate(0)
 * to translate(-50%). That only loops seamlessly when the track holds exactly
 * two identical halves AND one half is at least as wide/tall as its container.
 * With a short string ("SAVE THE DATE") on a wide viewport the half was much
 * narrower than the container, so the tail end of the track left a blank band
 * that swept through on every cycle and read as the marquee stopping and
 * starting again. Font swap (adineue PRO loads late) changed segment widths
 * after the animation had already started, which made it worse.
 *
 * HOW THIS FIXES IT
 * Markup ships ONE repeating unit (plus a static duplicate as a no-JS
 * fallback). At runtime we measure the unit, then clone it until the track
 * covers the container plus one full unit:
 *     copies = ceil(container / unit) + 1
 * We then animate exactly 0 -> -unit. After shifting by one unit the layout is
 * pixel-identical, so the loop is invisible and the track can never run out of
 * content. Duration is derived from the unit size so speed stays constant no
 * matter how long the text is or how wide the screen gets:
 *     duration = setting * (unit / container)
 * i.e. the setting means "seconds to travel one container length".
 *
 * Re-measures on: fonts ready, container resize (ResizeObserver — this is also
 * what handles the nav rail, which measures 0 until the overlay opens), and
 * Shopify theme-editor section reloads.
 */
(function () {
  if (window.CaMarquee) return;

  var RM = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  function unitSize(el, axis) {
    // offsetWidth/Height are layout boxes and ignore the rail's rotate(),
    // which is exactly what we want to measure against.
    return axis === 'y' ? el.offsetHeight : el.offsetWidth;
  }

  function layout(inst) {
    var el = inst.el;
    var track = inst.track;
    var axis = inst.axis;

    var container = unitSize(el, axis);
    if (!container) return; // hidden (e.g. closed nav overlay) — RO will call us back

    // Rebuild from the pristine single unit before measuring.
    track.style.animation = 'none';
    track.innerHTML = inst.unitHTML;

    var unit = unitSize(track.firstElementChild, axis);
    if (!unit) return; // fonts/styles not applied yet

    // +2, not +1: one copy covers the container, one is the spare that the
    // --ca-lead head-start parks on the leading edge (see the direction block
    // below), and the ceil() covers the remainder.
    var copies = Math.ceil(container / unit) + 2;
    if (copies < 3) copies = 3;

    function build(n) {
      var html = '';
      for (var i = 0; i < n; i++) html += inst.unitHTML;
      track.innerHTML = html;
    }
    build(copies);

    // Shift by the REAL distance between two copies, not by the unit's own box.
    // Any gap between copies (inline whitespace, a margin, inline-block
    // spacing) makes those two numbers differ, and shifting by the smaller one
    // leaves a visible jump on every loop. offsetTop/offsetLeft are layout
    // positions and ignore the rail's rotate(), same as offsetWidth/Height.
    var kids = track.children;
    if (kids.length > 1) {
      var delta = axis === 'y'
        ? kids[1].offsetTop - kids[0].offsetTop
        : kids[1].offsetLeft - kids[0].offsetLeft;
      if (delta > 0) unit = delta;
    }

    // The real spacing can exceed the box we sized `copies` from, so top up.
    var needed = Math.ceil(container / unit) + 2;
    if (needed > copies) { copies = needed; build(copies); }

    /* ---- Direction, measured rather than assumed -------------------------
     * The nav rail's wrapper carries rotate(180deg), which INVERTS the sign of
     * this translate: a local -1031px renders as a visual +1031px. The track
     * therefore drifted DOWN the rail while every clone was appended BELOW it,
     * so the padding sat in the direction the content was moving away from and
     * a blank band swept in from the top — up to 877px of a 1220px rail. No
     * number of clones can fix a direction error, which is why this survived
     * several rounds of "add more copies".
     *
     * So: measure. Apply a known inline transform, read the visual offset of
     * the track against its container, and derive the sign. We cannot read the
     * keyframe's own vars here because `animation:none` is set above, which
     * means the keyframe contributes no transform at all — hence the explicit
     * inline probe, which is cleared again immediately after.
     */
    function probe(px) {
      track.style.transform = axis === 'y'
        ? 'translate3d(0,' + px + 'px,0)'
        : 'translate3d(' + px + 'px,0,0)';
      var c = el.getBoundingClientRect();
      var t = track.getBoundingClientRect();
      return axis === 'y' ? t.top - c.top : t.left - c.left;
    }
    var gap0 = probe(0);           // resting offset — the rail sits ~102px low
    var gap1 = probe(unit);        // where one unit of local travel lands
    var dir = (gap1 - gap0) >= 0 ? 1 : -1;   // visual px per local px
    track.style.transform = '';

    // Park the leading visual edge exactly one unit before the container, so
    // the visible window is inside the cloned run for the whole cycle whichever
    // way it travels — and so the resting gap0 offset is absorbed too.
    var lead = (-unit - gap0) / dir;
    track.style.setProperty('--ca-lead', lead.toFixed(2) + 'px');

    // Set before the reduced-motion bail so a static rail is still covered.
    if (RM && RM.matches) { track.style.animation = 'none'; return; }

    track.style.setProperty('--ca-shift', '-' + unit + 'px');
    track.style.animation = '';
    track.style.animationDuration = (inst.seconds * (unit / container)).toFixed(3) + 's';
  }

  function init(scope) {
    var nodes = (scope || document).querySelectorAll('[data-ca-marquee]');
    Array.prototype.forEach.call(nodes, function (el) {
      var track = el.querySelector('[data-ca-marquee-track]');
      if (!track || !track.firstElementChild) return;

      var inst = el.__caMarq;
      if (!inst) {
        inst = el.__caMarq = {
          el: el,
          track: track,
          axis: el.getAttribute('data-ca-marquee-axis') === 'y' ? 'y' : 'x',
          seconds: parseFloat(el.getAttribute('data-ca-marquee-seconds')) || 20,
          // The first child is the repeating unit. Extra children in the
          // markup are only the no-JS fallback and get replaced below.
          unitHTML: track.firstElementChild.outerHTML
        };

        if (window.ResizeObserver) {
          var ro = new ResizeObserver(function () { layout(inst); });
          ro.observe(el);
        } else {
          window.addEventListener('resize', function () { layout(inst); });
        }
      }

      layout(inst);
    });
  }

  function boot(scope) {
    init(scope);
    // adineue PRO loads async; segment sizes change when it swaps in.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { init(scope); });
    }
    // Belt and braces for late layout (images above the marquee settling).
    window.addEventListener('load', function () { init(scope); });
  }

  window.CaMarquee = { init: init, refresh: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }

  // Theme editor: sections are torn down and re-injected on every edit.
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:select', function (e) { init(e.target); });
})();
